import { useState, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { COGNITO_DOMAIN, COGNITO_CLIENT_ID } from '@/api/config';
import { useIsMobile } from '@/hooks/useMediaQuery';

type AuthMode = 'signin' | 'signup' | 'confirm' | 'mfa' | 'webauthn';

const styles = {
  page: {
    width: '100vw',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg-app)',
    fontFamily: 'var(--font-family)',
  } as const,
  card: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-lg)',
    padding: '40px 36px',
    width: 380,
    maxWidth: '90vw',
    boxShadow: 'var(--shadow-md)',
  } as const,
  title: {
    color: 'var(--text-primary)',
    fontSize: 'var(--font-size-2xl)',
    fontWeight: 700,
    marginBottom: 8,
    marginTop: 0,
  } as const,
  subtitle: {
    color: 'var(--text-secondary)',
    fontSize: 'var(--font-size-base)',
    marginBottom: 24,
    marginTop: 0,
  } as const,
  label: {
    display: 'block',
    color: 'var(--text-secondary)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    marginBottom: 6,
  } as const,
  input: {
    width: '100%',
    padding: '10px 12px',
    background: 'var(--bg-app)',
    border: '1px solid var(--border-default)',
    borderRadius: 6,
    color: 'var(--text-primary)',
    fontSize: 'var(--font-size-base)',
    fontFamily: 'var(--font-family)',
    outline: 'none',
    boxSizing: 'border-box' as const,
    marginBottom: 16,
  } as const,
  button: {
    width: '100%',
    padding: '10px 16px',
    background: 'var(--color-primary)',
    color: '#ffffff',
    border: 'none',
    borderRadius: 6,
    fontSize: 'var(--font-size-base)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family)',
  } as const,
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  } as const,
  link: {
    color: 'var(--text-link)',
    fontSize: 'var(--font-size-sm)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    fontFamily: 'var(--font-family)',
    textDecoration: 'underline',
  } as const,
  error: {
    background: 'var(--bg-error)',
    color: 'var(--text-error)',
    padding: '10px 12px',
    borderRadius: 6,
    fontSize: 'var(--font-size-sm)',
    marginBottom: 16,
  } as const,
  footer: {
    textAlign: 'center' as const,
    marginTop: 20,
  } as const,
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    margin: '20px 0',
    color: 'var(--text-tertiary)',
    fontSize: 12,
  } as const,
  dividerLine: {
    flex: 1,
    height: 1,
    background: 'var(--bg-surface-raised)',
  } as const,
  socialButton: {
    width: '100%',
    padding: '10px 16px',
    background: 'var(--bg-surface-raised)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-strong)',
    borderRadius: 6,
    fontSize: 'var(--font-size-base)',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'var(--font-family)',
    marginBottom: 8,
  } as const,
};

function redirectToSocialProvider(identityProvider: string) {
  if (!COGNITO_DOMAIN || !COGNITO_CLIENT_ID) return;
  const redirectUri = window.location.origin + '/';
  const url =
    `https://${COGNITO_DOMAIN}/oauth2/authorize` +
    `?identity_provider=${encodeURIComponent(identityProvider)}` +
    `&client_id=${encodeURIComponent(COGNITO_CLIENT_ID)}` +
    `&response_type=code` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}` +
    `&scope=openid+email+profile`;
  window.location.href = url;
}

export function AuthPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { isLoading, needsConfirmation, needsMfaCode, needsWebAuthn, pendingEmail, error, clearError } =
    useAuthStore();
  const login = useAuthStore((s) => s.login);
  const signup = useAuthStore((s) => s.signup);
  const confirmCode = useAuthStore((s) => s.confirmCode);
  const submitMfaCode = useAuthStore((s) => s.submitMfaCode);
  const switchToWebAuthn = useAuthStore((s) => s.switchToWebAuthn);
  const submitWebAuthn = useAuthStore((s) => s.submitWebAuthn);

  const [mode, setMode] = useState<AuthMode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [mfaCode, setMfaCode] = useState('');

  const activeMode = needsWebAuthn ? 'webauthn' : needsMfaCode ? 'mfa' : needsConfirmation ? 'confirm' : mode;

  // Auto-trigger WebAuthn dialog when the challenge screen mounts
  useEffect(() => {
    if (activeMode === 'webauthn' && !isLoading) {
      void submitWebAuthn();
    }
  }, [activeMode, isLoading, submitWebAuthn]);

  function switchMode(newMode: AuthMode) {
    clearError();
    setMode(newMode);
    setPassword('');
    setCode('');
  }

  function handleSignIn(e: FormEvent) {
    e.preventDefault();
    void login(email, password);
  }

  function handleSignUp(e: FormEvent) {
    e.preventDefault();
    void signup(email, password, name);
  }

  function handleConfirm(e: FormEvent) {
    e.preventDefault();
    void confirmCode(pendingEmail || email, code);
  }

  return (
    <div style={{
      ...styles.page,
      minHeight: isMobile ? '100vh' : 'auto',
    }}>
      <div style={{
        ...styles.card,
        width: isMobile ? '100%' : 380,
        borderRadius: isMobile ? 0 : 'var(--radius-lg)',
        boxShadow: isMobile ? 'none' : 'var(--shadow-md)',
        minHeight: isMobile ? '100vh' : 'auto',
      }}>
        {activeMode === 'signin' && (
          <>
            <h1 style={styles.title}>{t('auth.signIn')}</h1>
            <p style={styles.subtitle}>{t('auth.signInSubtitle')}</p>
            {error && <div style={styles.error}>{error}</div>}
            <form onSubmit={handleSignIn}>
              <label style={styles.label}>{t('auth.email')}</label>
              <input
                style={styles.input}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                disabled={isLoading}
              />
              <label style={styles.label}>{t('auth.password')}</label>
              <input
                style={styles.input}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                disabled={isLoading}
              />
              <button
                type="submit"
                style={{
                  ...styles.button,
                  ...(isLoading ? styles.buttonDisabled : {}),
                }}
                disabled={isLoading}
              >
                {isLoading ? t('auth.signingIn') : t('auth.signIn')}
              </button>
            </form>
            <div style={styles.divider}>
              <div style={styles.dividerLine} />
              <span>{t('auth.or')}</span>
              <div style={styles.dividerLine} />
            </div>
            <button
              style={styles.socialButton}
              onClick={() => redirectToSocialProvider('Google')}
              type="button"
            >
              {t('auth.signInWithGoogle')}
            </button>
            <button
              style={styles.socialButton}
              onClick={() => redirectToSocialProvider('SignInWithApple')}
              type="button"
            >
              {t('auth.signInWithApple')}
            </button>
            <button
              style={styles.socialButton}
              onClick={() => redirectToSocialProvider('Facebook')}
              type="button"
            >
              {t('auth.signInWithFacebook')}
            </button>
            <button
              style={styles.socialButton}
              onClick={() => redirectToSocialProvider('LoginWithAmazon')}
              type="button"
            >
              {t('auth.signInWithAmazon')}
            </button>
            {/* GitHub login disabled -- GitHub OAuth is not compatible with Cognito OIDC */}
            <div style={styles.footer}>
              <button style={styles.link} onClick={() => switchMode('signup')}>
                {t('auth.createAccountLink')}
              </button>
            </div>
          </>
        )}

        {activeMode === 'signup' && (
          <>
            <h1 style={styles.title}>{t('auth.createAccount')}</h1>
            <p style={styles.subtitle}>
              {t('auth.createAccountSubtitle')}
            </p>
            {error && <div style={styles.error}>{error}</div>}
            <form onSubmit={handleSignUp}>
              <label style={styles.label}>{t('auth.name')}</label>
              <input
                style={styles.input}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="name"
                disabled={isLoading}
              />
              <label style={styles.label}>{t('auth.email')}</label>
              <input
                style={styles.input}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                disabled={isLoading}
              />
              <label style={styles.label}>{t('auth.password')}</label>
              <input
                style={styles.input}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                disabled={isLoading}
              />
              <button
                type="submit"
                style={{
                  ...styles.button,
                  ...(isLoading ? styles.buttonDisabled : {}),
                }}
                disabled={isLoading}
              >
                {isLoading ? t('auth.creatingAccount') : t('auth.createAccount')}
              </button>
            </form>
            <div style={styles.footer}>
              <button style={styles.link} onClick={() => switchMode('signin')}>
                {t('auth.alreadyHaveAccount')}
              </button>
            </div>
          </>
        )}

        {activeMode === 'mfa' && (
          <>
            <h1 style={styles.title}>{t('auth.mfaTitle')}</h1>
            <p style={styles.subtitle}>
              {t('auth.mfaSubtitle')}
            </p>
            {error && <div style={styles.error}>{error}</div>}
            <form onSubmit={(e) => { e.preventDefault(); void submitMfaCode(mfaCode); }}>
              <label style={styles.label}>{t('auth.authCode')}</label>
              <input
                style={styles.input}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                required
                autoComplete="one-time-code"
                disabled={isLoading}
                placeholder="123456"
                maxLength={6}
              />
              <button
                type="submit"
                style={{
                  ...styles.button,
                  ...(isLoading ? styles.buttonDisabled : {}),
                }}
                disabled={isLoading}
              >
                {isLoading ? t('auth.verifying') : t('auth.verify')}
              </button>
            </form>
            <div style={styles.footer}>
              <button style={styles.link} onClick={() => void switchToWebAuthn()} disabled={isLoading}>
                {t('auth.useSecurityKey')}
              </button>
              <span style={{ margin: '0 8px', color: 'var(--border-strong)' }}>|</span>
              <button style={styles.link} onClick={() => switchMode('signin')}>
                {t('auth.backToSignIn')}
              </button>
            </div>
          </>
        )}

        {activeMode === 'webauthn' && (
          <>
            <h1 style={styles.title}>{t('auth.webAuthnTitle')}</h1>
            <p style={styles.subtitle}>
              {t('auth.webAuthnSubtitle')}
            </p>
            {error && <div style={styles.error}>{error}</div>}
            <button
              style={{
                ...styles.button,
                ...(isLoading ? styles.buttonDisabled : {}),
              }}
              disabled={isLoading}
              onClick={() => void submitWebAuthn()}
            >
              {isLoading ? t('auth.waitingForKey') : t('auth.retryVerification')}
            </button>
            <div style={styles.footer}>
              <button
                style={styles.link}
                disabled={isLoading}
                onClick={() => {
                  // Re-authenticate with standard SRP flow to get TOTP challenge back
                  const pw = useAuthStore.getState()._pendingPassword;
                  const em = useAuthStore.getState().pendingEmail;
                  useAuthStore.setState({ needsWebAuthn: false, webAuthnCognitoUser: null, webAuthnChallengeOptions: null, error: null });
                  if (em && pw) void login(em, pw);
                }}
              >
                {t('auth.useAuthenticatorApp')}
              </button>
              <span style={{ margin: '0 8px', color: 'var(--border-strong)' }}>|</span>
              <button style={styles.link} onClick={() => switchMode('signin')}>
                {t('auth.backToSignIn')}
              </button>
            </div>
          </>
        )}

        {activeMode === 'confirm' && (
          <>
            <h1 style={styles.title}>{t('auth.verifyEmail')}</h1>
            <p style={styles.subtitle}>
              {t('auth.verifyEmailSubtitle', { email: pendingEmail || email })}
            </p>
            {error && <div style={styles.error}>{error}</div>}
            <form onSubmit={handleConfirm}>
              <label style={styles.label}>{t('auth.verificationCode')}</label>
              <input
                style={styles.input}
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoComplete="one-time-code"
                disabled={isLoading}
                placeholder="123456"
              />
              <button
                type="submit"
                style={{
                  ...styles.button,
                  ...(isLoading ? styles.buttonDisabled : {}),
                }}
                disabled={isLoading}
              >
                {isLoading ? t('auth.verifying') : t('auth.verify')}
              </button>
            </form>
            <div style={styles.footer}>
              <button style={styles.link} onClick={() => switchMode('signin')}>
                {t('auth.backToSignIn')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
